/**
 * Replays (docs/spec/06 §5): a run is its seed and one input per scan, so it can be stored as a short
 * string and played back through the engine to exactly the same end. Pure.
 */

import type { CaveState, Dir, Input } from '../sim/types';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** An input as a number 0-9: direction 0-4, plus 5 when fire is held. */
function code(i: Input): number {
  return i.dir + (i.fire ? 5 : 0);
}

function input(n: number): Input {
  return { dir: (n % 5) as Dir, fire: n >= 5 };
}

/**
 * Run-length encoded: each run is one character for the input (0-9) followed by its length in
 * base 64, one digit at a time, with runs longer than 63 split.
 */
export function encodeInputs(inputs: readonly Input[]): string {
  let out = '';
  let i = 0;
  while (i < inputs.length) {
    const c = code(inputs[i] ?? { dir: 0, fire: false });
    let n = 1;
    while (i + n < inputs.length && n < 63 && code(inputs[i + n] ?? { dir: 0, fire: false }) === c) n++;
    out += String(c) + (ALPHABET[n] ?? 'A');
    i += n;
  }
  return out;
}

/** Null when the text is not a valid encoding. */
export function decodeInputs(text: string): Input[] | null {
  if (text.length % 2 !== 0) return null;
  const out: Input[] = [];
  for (let i = 0; i < text.length; i += 2) {
    const c = Number(text[i]);
    const n = ALPHABET.indexOf(text[i + 1] ?? '');
    if (!Number.isInteger(c) || c < 0 || c > 9 || n < 1) return null;
    for (let k = 0; k < n; k++) out.push(input(c));
  }
  return out;
}

/** A fingerprint of everything that matters about a cave's state (FNV-1a, 32 bits, as hex). */
export function stateHash(s: CaveState): string {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h = Math.imul(h ^ (v & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((v >>> 8) & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((v >>> 16) & 0xff), 0x01000193) >>> 0;
  };
  for (const c of s.cells) mix(c);
  for (const v of [s.scan, s.score, s.diamonds, s.timeLeft, s.digby.x, s.digby.y, s.gateOpen ? 1 : 0]) mix(v);
  for (const ch of s.status) mix(ch.charCodeAt(0));
  return h.toString(16).padStart(8, '0');
}
